import { ValueSetterParams } from 'ag-grid-community'
import { isEmpty } from 'lodash'
import { setAlertQueue } from 'cache/alerts'
import { MessageType } from 'ts/enums/messageType.enums'

export const validateRationalNumberColumn = (newValue: any) => {
  const value = newValue.trim()

  if (value.length === 0 || /^-?(0|[1-9]\d*)?(\.\d+)?(?<=\d)$/.test(value)) {
    return value >= -1000 && value <= 100
  }
  return 'Invalid value.'
}

export const validatePercentColumn = (newValue: any) => {
  const value = String(newValue ?? '').trim()
  const newValueNumber = Number(value)

  if (value.length === 0) {
    return true
  }

  const isSimpleNumberString = (input: string) => {
    let index = 0
    let hasDigit = false
    let hasDot = false

    if (input.startsWith('-')) {
      index = 1
    }

    for (; index < input.length; index += 1) {
      const char = input[index]
      if (char >= '0' && char <= '9') {
        hasDigit = true
        continue
      }

      if (char === '.' && !hasDot) {
        hasDot = true
        continue
      }

      return false
    }

    return hasDigit
  }

  if (isSimpleNumberString(value)) {
    if (newValueNumber < -100 || newValueNumber > 100) {
      return 'Invalid value. Please enter a value between -100 and 100.'
    }
  } else {
    return 'Invalid value.'
  }

  return true
}

export const isValidPercentage = (value: any, acceptNull = false) => {
  if (acceptNull && isEmpty(value)) return true

  return value <= 100 && value >= -100
}

export const parseValidatedNumber = (newValue: any): number | null => {
  const parsed = Number(newValue)
  return Number.isNaN(parsed) ? null : parsed
}

export const stringToNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) {
    return 0
  }
  if (typeof value === 'number') {
    return value
  }
  const cleaned = value.replace(/,/g, '')
  const parsed = Number(cleaned)
  return Number.isNaN(parsed) ? 0 : parsed
}

export const parsePercentageEditValue = (
  value: number | string | null | undefined
): number | null => {
  const rawValue = typeof value === 'string' ? value.trim() : value

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null
  }

  const parsedValue = Number(rawValue)

  if (Number.isNaN(parsedValue) || parsedValue < -100 || parsedValue > 100) {
    return null
  }

  return parsedValue
}

export const roundNumberToFixedCommaWithDecimal = (value: any) => {
  const valueString = `${value}`.replace(/,/g, '')
  const parsed = parseFloat(valueString)

  const amount = parsed.toFixed(2)
  if (amount === 'NaN') {
    return ''
  }

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return formatter.format(Number(amount))
}

export const getTotalDiscountPercentage = (
  row: { highResellerAStandardDiscountPercentage?: number | string | null },
  value: number
) => {
  if (value === null) {
    return roundNumberToFixedCommaWithDecimal(value)
  }
  return roundNumberToFixedCommaWithDecimal(
    stringToNumber(row?.highResellerAStandardDiscountPercentage ?? 0) + stringToNumber(value)
  )
}

type ValidationResult = {
  isValid: boolean
  errorMessage?: string
}

const reportValidationError = (message?: string) => {
  setAlertQueue({
    type: MessageType.ERROR,
    message: message ?? 'Invalid value.',
  })
}

const applyValidationResult = <T>(ev: ValueSetterParams<T>, result?: ValidationResult): boolean => {
  if (!result?.isValid) {
    reportValidationError(result?.errorMessage)
    return false
  }

  if (ev.data && ev.colDef.field) {
    const parsedValue = parseValidatedNumber(ev.newValue)
    ;(ev.data as Record<string, unknown>)[ev.colDef.field] = parsedValue ?? ev.newValue
  }

  return true
}

export const validateBdNetValue = (ev: ValueSetterParams): boolean => {
  const rawValue = typeof ev.newValue === 'string' ? ev.newValue.trim() : ev.newValue

  if (rawValue === null || rawValue === undefined) {
    return applyValidationResult(ev, { isValid: false, errorMessage: 'Invalid value.' })
  }

  if (typeof rawValue === 'string' && rawValue.length === 0) {
    if (ev.data && ev.colDef.field) {
      ;(ev.data as Record<string, unknown>)[ev.colDef.field] = null
    }
    return true
  }

  if (typeof rawValue === 'string') {
    const isDigits = (value: string) => /^\d+$/.test(value)

    const hasValidFraction = (fractionalPart?: string) => {
      if (fractionalPart === undefined || fractionalPart.length === 0) {
        return true
      }
      return isDigits(fractionalPart)
    }

    const hasValidGroupedInteger = (integerPart: string) => {
      if (!integerPart.includes(',')) {
        return integerPart.length === 0 || isDigits(integerPart)
      }

      const groups = integerPart.split(',')
      if (groups.some((group) => group.length === 0)) {
        return false
      }

      if (groups[0].length < 1 || groups[0].length > 3 || !isDigits(groups[0])) {
        return false
      }

      return groups.slice(1).every((group) => group.length === 3 && isDigits(group))
    }

    const isValidBdNetString = (input: string) => {
      if (input.length === 0) {
        return false
      }

      const normalized = input.startsWith('-') ? input.slice(1) : input
      const parts = normalized.split('.')
      if (parts.length > 2) {
        return false
      }

      const integerPart = parts[0]
      const fractionalPart = parts[1]
      const hasIntegerDigits = integerPart.length > 0
      const hasFractionDigits = fractionalPart !== undefined && fractionalPart.length > 0

      if (!hasIntegerDigits && !hasFractionDigits) {
        return false
      }

      return hasValidFraction(fractionalPart) && hasValidGroupedInteger(integerPart)
    }

    if (!isValidBdNetString(rawValue)) {
      return applyValidationResult(ev, { isValid: false, errorMessage: 'Invalid Value' })
    }

    const cleanedValue = rawValue.replace(/,/g, '')
    const parsedValue = parseValidatedNumber(cleanedValue)
    if (parsedValue === null) {
      return applyValidationResult(ev, { isValid: false, errorMessage: 'Invalid Value' })
    }

    return applyValidationResult(ev, { isValid: true })
  }

  if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
    return applyValidationResult(ev, { isValid: true })
  }

  return applyValidationResult(ev, { isValid: false, errorMessage: 'Invalid Value' })
}

const Validate: Record<string, (ev: ValueSetterParams) => ValidationResult> = {
  requestNetMarginPercentage: (ev) => {
    const percentResult = validatePercentColumn(ev.newValue)
    if (percentResult !== true) {
      return {
        isValid: false,
        errorMessage: typeof percentResult === 'string' ? percentResult : 'Invalid value.',
      }
    }
    return { isValid: true }
  },
}

export const ValidationHandle = (ev: ValueSetterParams) => {
  const colId = ev.column.getColId()
  if (!colId) {
    return false
  }

  if (colId === 'requestNetMarginPercentage') {
    const rawValue = typeof ev.newValue === 'string' ? ev.newValue.trim() : ev.newValue
    if (rawValue === '') {
      if (ev.data && ev.colDef.field) {
        ;(ev.data as Record<string, unknown>)[ev.colDef.field] = null
      }
      return true
    }
  }

  const validationResult = Validate[colId]?.(ev)
  return applyValidationResult(ev, validationResult)
}

export const validateOgmPercentValue = (ev: ValueSetterParams): boolean => {
  const rawValue = typeof ev.newValue === 'string' ? ev.newValue.trim() : ev.newValue
  if (rawValue === '') {
    if (ev.data && ev.colDef.field) {
      ;(ev.data as Record<string, unknown>)[ev.colDef.field] = null
    }
    return true
  }

  const percentResult = validatePercentColumn(ev.newValue)
  if (percentResult !== true) {
    return applyValidationResult(ev, {
      isValid: false,
      errorMessage: typeof percentResult === 'string' ? percentResult : 'Invalid value.',
    })
  }

  return applyValidationResult(ev, { isValid: true })
}
